import React, { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { message } from 'antd';
import chatStore from '../stores/chatStore';
import { useTheme } from '../theme/ThemeContext';

function isIOS() {
    if (typeof navigator === 'undefined') return false;
    return (
        /iPad|iPhone|iPod/.test(navigator.userAgent) ||
        (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
    );
}

function pickMimeType() {
    if (typeof MediaRecorder === 'undefined') return null;
    const candidates = isIOS()
        ? ['audio/mp4', 'audio/aac', 'audio/x-m4a', 'video/mp4', 'audio/webm']
        : [
              'audio/webm;codecs=opus',
              'audio/webm',
              'audio/mp4',
              'audio/ogg;codecs=opus',
              'audio/ogg',
          ];
    for (const type of candidates) {
        try {
            if (MediaRecorder.isTypeSupported?.(type)) return type;
        } catch (_) {
            /* ignore */
        }
    }
    return '';
}

function extFromMime(mime) {
    const base = String(mime || '').split(';')[0].trim().toLowerCase();
    if (base.includes('ogg')) return 'ogg';
    if (base.includes('mp4') || base.includes('m4a') || base.includes('aac')) return 'm4a';
    if (base.includes('wav')) return 'wav';
    if (base.includes('mpeg') || base.includes('mp3')) return 'mp3';
    return 'webm';
}

function formatElapsed(sec) {
    const s = Math.max(0, Math.floor(sec));
    const m = Math.floor(s / 60);
    const r = s % 60;
    return `${m}:${String(r).padStart(2, '0')}`;
}

function getUserMedia(constraints) {
    const n = navigator;
    if (n.mediaDevices?.getUserMedia) {
        return n.mediaDevices.getUserMedia(constraints);
    }
    const legacy =
        n.getUserMedia || n.webkitGetUserMedia || n.mozGetUserMedia || n.msGetUserMedia;
    if (!legacy) {
        const err = new Error('getUserMedia missing');
        err.name = 'NotSupportedError';
        return Promise.reject(err);
    }
    return new Promise((resolve, reject) => {
        legacy.call(n, constraints, resolve, reject);
    });
}

function encodeWav(chunks, sampleRate) {
    let length = 0;
    for (const chunk of chunks) length += chunk.length;
    const samples = new Float32Array(length);
    let offset = 0;
    for (const chunk of chunks) {
        samples.set(chunk, offset);
        offset += chunk.length;
    }
    const buffer = new ArrayBuffer(44 + samples.length * 2);
    const view = new DataView(buffer);
    const writeStr = (pos, str) => {
        for (let i = 0; i < str.length; i += 1) view.setUint8(pos + i, str.charCodeAt(i));
    };
    writeStr(0, 'RIFF');
    view.setUint32(4, 36 + samples.length * 2, true);
    writeStr(8, 'WAVE');
    writeStr(12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true);
    view.setUint16(22, 1, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * 2, true);
    view.setUint16(32, 2, true);
    view.setUint16(34, 16, true);
    writeStr(36, 'data');
    view.setUint32(40, samples.length * 2, true);
    let idx = 44;
    for (let i = 0; i < samples.length; i += 1) {
        const s = Math.max(-1, Math.min(1, samples[i]));
        view.setInt16(idx, s < 0 ? s * 0x8000 : s * 0x7fff, true);
        idx += 2;
    }
    return new Blob([buffer], { type: 'audio/wav' });
}

function startWavRecorder(stream) {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return null;
    const ctx = new Ctx();
    const source = ctx.createMediaStreamSource(stream);
    const processor = ctx.createScriptProcessor(4096, 1, 1);
    const chunks = [];
    processor.onaudioprocess = (event) => {
        chunks.push(new Float32Array(event.inputBuffer.getChannelData(0)));
    };
    source.connect(processor);
    processor.connect(ctx.destination);
    if (ctx.state === 'suspended') ctx.resume?.();
    return {
        stop: async () => {
            try {
                processor.disconnect();
                source.disconnect();
            } catch (_) {
                /* ignore */
            }
            const blob = encodeWav(chunks, ctx.sampleRate);
            try {
                await ctx.close();
            } catch (_) {
                /* ignore */
            }
            return blob;
        },
    };
}

const IconMic = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
        <rect x="9" y="3" width="6" height="11" rx="3" stroke="currentColor" strokeWidth="2" />
        <path
            d="M5 11a7 7 0 0014 0M12 18v3"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
        />
    </svg>
);

const IconStopRec = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
        <rect x="7" y="7" width="10" height="10" rx="2" />
    </svg>
);

/**
 * Запись с микрофона → upload как audio-вложение.
 * Показывается только если модель принимает audio.
 */
const AudioMicButton = observer(() => {
    const { theme } = useTheme();
    const isDark = theme === 'dark';
    const [recording, setRecording] = useState(false);
    const [elapsed, setElapsed] = useState(0);
    const mediaRef = useRef(null);
    const recorderRef = useRef(null);
    const chunksRef = useRef([]);
    const timerRef = useRef(null);
    const startedAtRef = useRef(0);
    const wavRecorderRef = useRef(null);

    const stopTracks = () => {
        const stream = mediaRef.current;
        if (stream) {
            stream.getTracks().forEach((t) => t.stop());
            mediaRef.current = null;
        }
    };

    const clearTimer = () => {
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }
    };

    useEffect(() => {
        return () => {
            clearTimer();
            try {
                recorderRef.current?.stop();
            } catch (_) {
                /* ignore */
            }
            try {
                wavRecorderRef.current?.stop();
            } catch (_) {
                /* ignore */
            }
            wavRecorderRef.current = null;
            stopTracks();
        };
    }, []);

    const finishRecording = async (blob, mime) => {
        if (!blob || blob.size < 64) {
            message.warning('Запись слишком короткая');
            return;
        }
        const ext = extFromMime(mime || blob.type);
        const file = new File(
            [blob],
            `voice-${new Date().toISOString().replace(/[:.]/g, '-')}.${ext}`,
            { type: mime || blob.type || 'audio/webm' }
        );
        await chatStore.uploadAttachment(file);
    };

    const stopRecording = () => {
        clearTimer();
        setRecording(false);
        setElapsed(0);
        const wavRecorder = wavRecorderRef.current;
        if (wavRecorder) {
            wavRecorderRef.current = null;
            wavRecorder
                .stop()
                .then((blob) => finishRecording(blob, 'audio/wav'))
                .catch((e) => message.error(e.message || 'Не удалось сохранить запись'))
                .finally(() => stopTracks());
            return;
        }
        const recorder = recorderRef.current;
        if (recorder && recorder.state !== 'inactive') {
            try {
                recorder.stop();
            } catch (_) {
                stopTracks();
            }
        } else {
            stopTracks();
        }
    };

    const startRecording = async () => {
        if (typeof window === 'undefined') return;
        if (!window.isSecureContext) {
            message.error('Микрофон доступен только по HTTPS. Откройте сайт по защищённому адресу.');
            return;
        }
        try {
            const stream = await getUserMedia({
                audio: {
                    echoCancellation: true,
                    noiseSuppression: true,
                },
            }).catch(() => getUserMedia({ audio: true }));
            mediaRef.current = stream;

            const beginTimer = () => {
                startedAtRef.current = Date.now();
                setElapsed(0);
                setRecording(true);
                timerRef.current = setInterval(() => {
                    setElapsed(Math.floor((Date.now() - startedAtRef.current) / 1000));
                }, 250);
            };

            if (typeof MediaRecorder !== 'undefined') {
                const mimeType = pickMimeType();
                const options = mimeType ? { mimeType } : undefined;
                let recorder;
                try {
                    recorder = new MediaRecorder(stream, options);
                } catch (_) {
                    recorder = new MediaRecorder(stream);
                }
                chunksRef.current = [];
                recorderRef.current = recorder;

                recorder.ondataavailable = (e) => {
                    if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
                };
                recorder.onerror = () => {
                    message.error('Ошибка записи');
                    stopRecording();
                };
                recorder.onstop = async () => {
                    const type = recorder.mimeType || mimeType || 'audio/webm';
                    const blob = new Blob(chunksRef.current, { type });
                    chunksRef.current = [];
                    recorderRef.current = null;
                    stopTracks();
                    clearTimer();
                    setRecording(false);
                    setElapsed(0);
                    try {
                        await finishRecording(blob, type);
                    } catch (e) {
                        message.error(e.message || 'Не удалось сохранить запись');
                    }
                };

                if (isIOS()) recorder.start();
                else recorder.start(250);
                beginTimer();
                return;
            }

            const wavRecorder = startWavRecorder(stream);
            if (!wavRecorder) {
                stopTracks();
                message.error('Запись аудио не поддерживается в этом браузере');
                return;
            }
            wavRecorderRef.current = wavRecorder;
            beginTimer();
        } catch (e) {
            stopTracks();
            const denied =
                e?.name === 'NotAllowedError' ||
                e?.name === 'PermissionDeniedError' ||
                /permission|denied/i.test(String(e?.message || ''));
            const missing =
                e?.name === 'NotSupportedError' ||
                e?.name === 'NotFoundError' ||
                /getUserMedia missing/i.test(String(e?.message || ''));
            message.error(
                denied
                    ? 'Нет доступа к микрофону. Разрешите запись в настройках браузера или приложения.'
                    : missing
                      ? 'Микрофон не найден или недоступен. Проверьте, что открыт HTTPS и разрешён доступ.'
                      : e?.message || 'Не удалось начать запись'
            );
        }
    };

    const onClick = () => {
        if (recording) stopRecording();
        else startRecording();
    };

    return (
        <button
            type="button"
            title={recording ? 'Остановить запись' : 'Записать аудио'}
            aria-label={recording ? 'Остановить запись' : 'Записать аудио'}
            aria-pressed={recording}
            onClick={onClick}
            className={`flex h-9 items-center justify-center gap-1.5 rounded-xl px-2.5 text-xs font-medium transition ${
                recording
                    ? 'bg-red-600 text-white hover:bg-red-500'
                    : isDark
                      ? 'hover:bg-white/10'
                      : 'hover:bg-black/5'
            }`}
        >
            {recording ? <IconStopRec /> : <IconMic />}
            {recording ? (
                <span className="tabular-nums">{formatElapsed(elapsed)}</span>
            ) : null}
        </button>
    );
});

export default AudioMicButton;
